import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Tabs, Tab, Paper } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import AvailableShiftsContainer from './containers/AvailableShiftsContainer'
import MyShiftsContainer from './containers/MyShiftsContainer'
import { MAIN_HEADER_HEIGHT } from '@/layouts/main'

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`shift-marketplace-tabpanel-${index}`}
            aria-labelledby={`shift-marketplace-tab-${index}`}
            {...other}
            style={{ height: '100%' }}
        >
            {value === index && (
                <Box sx={{ height: '100%' }}>
                    {children}
                </Box>
            )}
        </div>
    )
}

function a11yProps(index: number) {
    return {
        id: `shift-marketplace-tab-${index}`,
        'aria-controls': `shift-marketplace-tabpanel-${index}`,
    }
}

export function ShiftMarketplacePage() {
    const { t } = useTranslation()
    const [tabValue, setTabValue] = useState(0)

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue)
    }

    return (
        <Box sx={{
            // 100dvh (not 100vh) so the mobile browser/WebView address bar doesn't clip the
            // content; subtract the real header height (was a hard-coded 64, header is 56).
            height: `calc(100dvh - ${MAIN_HEADER_HEIGHT}px)`,
            display: 'flex',
            flexDirection: 'column',
            pt: 2,
            px: 2,
            overflow: 'hidden'
        }} data-testid="shift-marketplace-page">
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h5" component="h1">
                        {t('page.shiftMarketplace.title', 'Shift Marketplace')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('page.shiftMarketplace.description', 'Browse and claim available shifts or manage your claimed shifts.')}
                    </Typography>
                </Box>
            </Box>

            <Paper sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: 1
            }} data-testid="shift-marketplace-shell">
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="shift marketplace tabs"
                        variant="fullWidth"
                    >
                        <Tab
                            icon={<SearchIcon />}
                            iconPosition="start"
                            label={t('page.shiftMarketplace.availableShifts', 'Available Shifts')}
                            {...a11yProps(0)}
                        />
                        <Tab
                            icon={<AssignmentIndIcon />}
                            iconPosition="start"
                            label={t('page.shiftMarketplace.myShifts', 'My Shifts')}
                            {...a11yProps(1)}
                        />
                    </Tabs>
                </Box>

                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <TabPanel value={tabValue} index={0}>
                        <AvailableShiftsContainer />
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <MyShiftsContainer />
                    </TabPanel>
                </Box>
            </Paper>
        </Box>
    )
}

export default ShiftMarketplacePage
